import { IonApp, IonRouterOutlet, IonSplitPane, setupIonicReact, IonRefresher, IonRefresherContent, IonContent, RefresherEventDetail } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route, Switch, Redirect } from 'react-router-dom';
import Menu from './components/Menu';
import Page from './pages/Page';
import { ServerUrlProvider } from './contexts/ServerUrlProvider';
import { AzureAuthProvider, AzureAuthCallback } from './adf';
import { getConfig } from './helpers/configHelper';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './theme/base.scss';
import Login from './pages/Login';
import Calendar from './pages/Calendar';
import Vigilancia from './pages/Vigilancia';
import ActivateAccount from './pages/ActivateAccount';
import ResetPassword from './components/ResetPassword';
import ResetPasswordConfirm from './components/ResetPasswordConfirm';

import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import React, { useEffect, useCallback, useState } from "react"

import { deviceFCMToken, tokenUpdate } from './actions/loginInfo';
import { useAppDispatch } from './app/hooks';
import { sessionManager } from './helpers/sessionManager';
import { getWorkerInfo } from './actions/workerInfo';
import {LocalNotifications} from '@capacitor/local-notifications'
import { App as AppFunc, AppState }from '@capacitor/app';
import { open } from './helpers/open';
import Notifs from './pages/Notifs';
import MiSalud from './pages/MiSalud';
import { Capacitor } from '@capacitor/core';

import { Server } from 'http';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import Prevencion from './pages/Prevencion';
import SignReport from './pages/SignReport';
// import WebSign from './pages/WebSign';
import PDFPreview from './pages/PDFPreview';
import QuestionnaireReport from './pages/QuestionnaireReport';

setupIonicReact();

const TOKEN_UPDATE_TIMEOUT = 24 * 60 * 60 * 1000; //in milliseconds
const REFRESH_UPDATE_TIMEOUT = 3 * 60 * 1000; //in milliseconds

const App: React.FC = () => {

  const dispatch = useAppDispatch();

  
  const initNotifs = useCallback(() => {
    // Request permission to use push notifications
    // iOS will prompt user and return if they granted permission or not
    // Android will just grant without prompting
    PushNotifications.requestPermissions().then(result => {
      if (result.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        PushNotifications.register();
      } else {
        // Show some error
        throw new Error('User denied permissions!'); 
      }
    });
    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', (token: Token) => {
      console.log('Push registration success, token: ' + token.value);
      // alert("Devicetoken")
      dispatch(deviceFCMToken(token.value))
    }
    );

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error: any) => {
      console.warn('Error on registration: ' + JSON.stringify(error));
    }
    );

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.warn('Push received: ' + JSON.stringify(notification));
    }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.warn('Push action performed: ' + JSON.stringify(notification));
    }
    );
  }, [dispatch]);


  function handleRefresh(event: CustomEvent<RefresherEventDetail>) {
    console.log("Handle refresh")
    dispatch(getWorkerInfo())
    setTimeout(() => {
      event.detail.complete();
    }, 1000);
  }

  const updateRefreshTimeout = useCallback(() => {
    const timeoutId = setTimeout(() => {
      if (localStorage.getItem("logged") === "true") {
        dispatch(getWorkerInfo())
        updateRefreshTimeout()
      }
    }, REFRESH_UPDATE_TIMEOUT)
    return timeoutId;
  }, [dispatch]);
  
  const tokenUpdateTimeout = useCallback(() => {
    const timeoutId = setTimeout(() => {
      if (localStorage.getItem("logged") === "true") {
        dispatch(tokenUpdate())
        tokenUpdateTimeout()
      }
    }, TOKEN_UPDATE_TIMEOUT)
    return timeoutId;
  }, [dispatch]);
  
  useEffect(() => {
    let tokenTimeoutId: NodeJS.Timeout;
    let refreshTimeoutId: NodeJS.Timeout;
  
    const isPush = Capacitor.isPluginAvailable('PushNotifications')
    if (isPush) {
      console.log("Plugin available")
      initNotifs()
    }
    // Evitar llamadas que puedan forzar expiración si no hay sesión aún
    if (localStorage.getItem("logged") === "true") {
      dispatch(getWorkerInfo())
    }
    LocalNotifications.addListener('localNotificationActionPerformed', (payload) => {
      const filepath = payload.notification.extra.filepath;
      open(filepath)
    });
  
    if (localStorage.getItem("logged") === "true") {
      dispatch(tokenUpdate())
      tokenUpdateTimeout()
      updateRefreshTimeout()
      
      // Iniciar monitoreo de sesión unificado
      sessionManager.startSessionMonitoring()
    }
  
    const platform = Capacitor.getPlatform()
    let visibilityListener: any;
    let appStateListener: any;
  
    if (platform === "web") {
      visibilityListener = () => {
        if (!document.hidden) {
          if (localStorage.getItem("logged") === "true") {
            dispatch(tokenUpdate())
            tokenTimeoutId = tokenUpdateTimeout()
            refreshTimeoutId = updateRefreshTimeout()
          }
        } else {
          dispatch(tokenUpdate())
          tokenTimeoutId = tokenUpdateTimeout()
          refreshTimeoutId = updateRefreshTimeout()
        }
      };
      document.addEventListener('visibilitychange', visibilityListener);
    } else {
      appStateListener = AppFunc.addListener('appStateChange', (state: AppState) => {
        if (state.isActive === true) {
          if (localStorage.getItem("logged") === "true") {
            dispatch(tokenUpdate())
            tokenTimeoutId = tokenUpdateTimeout()
            refreshTimeoutId = updateRefreshTimeout()
          }
        } else {
          clearTimeout(tokenTimeoutId)
        }
      });
    }
  
    return () => {
      LocalNotifications.removeAllListeners()
      clearTimeout(tokenTimeoutId)
      clearTimeout(refreshTimeoutId)
      if (platform === "web") {
        document.removeEventListener('visibilitychange', visibilityListener);
      } else {
        appStateListener?.remove();
      }
    };
  }, [dispatch, tokenUpdateTimeout, updateRefreshTimeout, initNotifs]); // Add dependencies

  // Verificar si está habilitada la autenticación ADF
  const [config, setConfig] = useState<any>(null);
  const [useAdfAuth, setUseAdfAuth] = useState(false);

  // Cargar configuración al inicio
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const appConfig = await getConfig();
        setConfig(appConfig);
        setUseAdfAuth(appConfig.USE_ADF_AUTH === true);
        // Persistir server_url si no existe aún en localStorage
        if (appConfig?.SERVER_URL && !localStorage.getItem('server_url')) {
          localStorage.setItem('server_url', appConfig.SERVER_URL);
        }
      } catch (error) {
        console.error('Error cargando configuración:', error);
        setUseAdfAuth(false);
      }
    };
    loadConfig();
  }, []);

  const renderAppContent = () => (
    <IonReactRouter>
      <Switch>
        <Route path="/login" exact={true} >
          <Login/>
        </Route>
        {useAdfAuth && (
          <Route path="/callback" exact={true} >
            <AzureAuthCallback/>
          </Route>
        )}
        <Route  path="/activate_account/:uuid/:token" exact={true}>
          <ActivateAccount/>
        </Route>

        {!useAdfAuth && (
          <Route path="/password_reset_custom" exact={true}>
            <ResetPassword/>
          </Route>
        )}
        {!useAdfAuth && (
          <Route path="/reset_password/:uid/:token" exact={true}>
            <ResetPasswordConfirm/>
          </Route>
        )}

        <Route path="/">
          <IonSplitPane contentId="main">
            <Menu />
            <IonRouterOutlet id="main">
              <Route path="/" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /', {pathname: props.location.pathname, raw, ok}); return ok ? <Home /> : <Redirect to="/login" /> }} />
              <Route path="/page/:name" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /page/:name', {pathname: props.location.pathname, raw, ok}); return ok ? <Page {...props} /> : <Redirect to="/login" /> }} />
              <Route path="/vigilancia" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /vigilancia', {pathname: props.location.pathname, raw, ok}); return ok ? <Vigilancia /> : <Redirect to="/login" /> }} />
              <Route path="/notificaciones" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /notificaciones', {pathname: props.location.pathname, raw, ok}); return ok ? <Notifs /> : <Redirect to="/login" /> }} />
              <Route path="/mi-salud" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /mi-salud', {pathname: props.location.pathname, raw, ok}); return ok ? <MiSalud /> : <Redirect to="/login" /> }} />
              <Route path="/agenda" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /agenda', {pathname: props.location.pathname, raw, ok}); return ok ? <Calendar /> : <Redirect to="/login" /> }} />
              <Route path="/informacion-de-prevencion" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /informacion-de-prevencion', {pathname: props.location.pathname, raw, ok}); return ok ? <Prevencion /> : <Redirect to="/login" /> }} />

              <Route path="/firmar/report/:type/:id" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /firmar/report/:type/:id', {pathname: props.location.pathname, raw, ok}); return ok ? <SignReport {...props} /> : <Redirect to="/login" /> }} />
              <Route path="/view/report/:type/:id" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /view/report/:type/:id', {pathname: props.location.pathname, raw, ok}); return ok ? <PDFPreview {...props} /> : <Redirect to="/login" /> }} />
              <Route path="/questionnaire/:type/:id" exact={true} render={(props)=> { const raw = localStorage.getItem('logged')||''; const ok = /^(true|1)$/i.test(raw); console.debug('[RouteGuard] /questionnaire/:type/:id', {pathname: props.location.pathname, raw, ok}); return ok ? <QuestionnaireReport {...props} /> : <Redirect to="/login" /> }} />

              {/* Sin fallback aquí para no interferir con el matching de Ionic */}
            </IonRouterOutlet>
          </IonSplitPane>
        </Route>
        {/* Fallback global al final */}
        <Route component={NotFound} />
      </Switch>
    </IonReactRouter>
  );

  return (
    <IonApp>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <ServerUrlProvider>
          {useAdfAuth ? (
            <AzureAuthProvider>
              {renderAppContent()}
            </AzureAuthProvider>
          ) : (
            renderAppContent()
          )}
        </ServerUrlProvider>
      </IonContent>
    </IonApp>
  );
};

export default App;
